/* eslint-disable */
const detectNewLine = require('detect-newline');
const htmlparser = require('htmlparser2');
const { List, Stack, Set } = require('immutable');
const { Document } = require('slate');
const isUrl = require('is-url');

const {
  videoInfoFromURL,
  videoInfoToURL,
} = require('libs/editor/views/mediaInput');

const {
  BLOCKS,
  INLINES,
  MARKS,
  CONTAINERS,
  VOID,
  LEAFS,
  Block,
  Inline,
  Text,
  Mark,
} = require('@wikifactory/markup-it/lib');

// FIXME - Instead of importing the whole file from markup-it, patch only the
// required function (parse) so it can handle iframes

INLINES.VIDEO = 'video';
VOID[INLINES.VIDEO] = true;

const INLINE_TAGS = {
  a: INLINES.LINK,
  img: INLINES.IMAGE,
  iframe: INLINES.VIDEO,
};

const BLOCK_TAGS = {
  h1: BLOCKS.HEADING_1,
  h2: BLOCKS.HEADING_2,
  h3: BLOCKS.HEADING_3,
  h4: BLOCKS.HEADING_4,
  h5: BLOCKS.HEADING_5,
  h6: BLOCKS.HEADING_6,
  pre: BLOCKS.CODE,
  blockquote: BLOCKS.BLOCKQUOTE,
  p: BLOCKS.PARAGRAPH,
  hr: BLOCKS.HR,

  table: BLOCKS.TABLE,
  tr: BLOCKS.TABLE_ROW,
  th: BLOCKS.TABLE_CELL,
  td: BLOCKS.TABLE_CELL,

  ul: BLOCKS.UL_LIST,
  ol: BLOCKS.OL_LIST,
  li: BLOCKS.LIST_ITEM,
};

const MARK_TAGS = {
  b: MARKS.BOLD,
  strong: MARKS.BOLD,
  del: MARKS.STRIKETHROUGH,
  em: MARKS.ITALIC,
  code: MARKS.CODE,
};

const MARK_CLASSNAME = {
  'line-through': MARKS.STRIKETHROUGH,
};

const TAGS_TO_DATA = {
  a(attribs) {
    return {
      href: attribs.href,
      title: attribs.alt || '',
    };
  },
  img(attribs) {
    return {
      src: attribs.src,
      alt: attribs.alt || '',
    };
  },
  iframe(attribs) {
    // FIXME - Sometimes attribs arrive completely broken.
    // Maybe related to going back and forth between HTML and MD,
    const site = attribs.site || attribs.id || 'unknown';
    if (site === 'unknown') {
      return { src: '', site };
    }
    const videoInfo = isUrl(attribs.src)
      ? videoInfoFromURL(attribs.src)
      : videoInfoFromURL(videoInfoToURL(attribs.src, site));
    let src = false;
    if (videoInfo && site === videoInfo.site) {
      src = videoInfo.key;
    }
    return {
      src,
      site,
    };
  },
  h1: resolveHeadingAttrs,
  h2: resolveHeadingAttrs,
  h3: resolveHeadingAttrs,
  h4: resolveHeadingAttrs,
  h5: resolveHeadingAttrs,
  h6: resolveHeadingAttrs,
};

function resolveHeadingAttrs(attribs) {
  return attribs.id ? { id: attribs.id } : {};
}

/**
 * Flatten a block node into a list of inline nodes.
 * @param  {Node} node
 * @return {List<Node>} nodes
 */
function selectInlines(node) {
  if (node.object !== 'block') {
    return List([node]);
  }

  const { nodes } = node;
  return nodes.reduce(
    (result, child) => result.concat(selectInlines(child)),
    List()
  );
}

/**
 * Get all marks from a class name.
 * @param {String} className
 * @return {Array<Mark>}
 */
function getMarksForClassName(className) {
  className = className || '';
  const classNames = className.split(' ');
  const result = [];

  classNames.forEach(name => {
    const type = MARK_CLASSNAME[name];
    if (!type) {
      return;
    }

    const mark = Mark.create({
      type,
    });
    result.push(mark);
  });

  return result;
}

/**
 * Returns the accepted block types for the given container
 */
function acceptedBlocks(container) {
  return CONTAINERS[container.type || container.object];
}

/**
 * True if the node is a block container node
 */
function isBlockContainer(node) {
  return Boolean(acceptedBlocks(node));
}

/**
 * Returns the default block type for a block container
 */
function defaultBlockType(container) {
  return acceptedBlocks(container)[0];
}

/**
 * True if `block` can contain `node`
 */
function canContain(block, node) {
  if (node.object === 'inline' || node.object === 'text') {
    return LEAFS[block.type];
  }
  const types = acceptedBlocks(block);
  return types && types.indexOf(node.type) !== -1;
}

/*
 * sanitizeSpaces replace non-breaking spaces with regular space
 * non-breaking spaces (aka &nbsp;) are sources of many problems & quirks
 * &nbsp; in ascii is `0xA0` or `0xC2A0` in utf8
 * @param {String} str
 * @return {String}
 */
function sanitizeSpaces(str) {
  return str.replace(/\xa0/g, ' ');
}

/**
 * @param {String} tagName The tag name
 * @param {Object} attrs The tag's attributes
 * @return {Object} data
 */
function getData(tagName, attrs) {
  return (TAGS_TO_DATA[tagName] || (() => {}))(attrs);
}

/**
 * @param {String} nodeType
 * @return {Boolean} isVoid
 */
function isVoid(nodeType) {
  return Boolean(VOID[nodeType]);
}

/**
 * Returns the list of lines in the string
 * @param {String} text
 * @param {String} sep?
 * @return {List<String>}
 */
function splitLines(text, sep) {
  sep = sep || detectNewLine(text) || '\n';
  return List(text.split(sep));
}

/**
 * Parse an HTML string into a document
 * @param {String} str
 * @return {Document}
 */
function parse(str) {
  // For convenience, starts with a root node
  const root = Document.create({});
  // The top of the stack always hold the current parent
  // node. Should never be empty.
  let stack = Stack().push(root);
  // The current marks
  let marks = Set();

  // Update the node on top of the stack with the given node
  function setNode(node) {
    stack = stack.pop().push(node);
  }

  // Append a node child to the current parent node
  function appendNode(node) {
    const parent = stack.peek();
    let { nodes } = parent;

    // If parent is not a block container
    if (!isBlockContainer(parent) && node.object == 'block') {
      // Discard all blocks
      nodes = nodes.concat(selectInlines(node));
    }

    // Wrap node if type is not allowed
    else if (
      isBlockContainer(parent) &&
      (node.object !== 'block' || !canContain(parent, node))
    ) {
      const previous = parent.nodes.last();
      if (previous && canContain(previous, node)) {
        // Reuse previous block if possible
        nodes = nodes
          .pop()
          .push(previous.set('nodes', previous.nodes.push(node)));
      } else {
        // Else insert a default wrapper
        node = Block.create({
          type: defaultBlockType(parent),
          nodes: [node],
        });

        nodes = nodes.push(node);
      }
    } else {
      nodes = nodes.push(node);
    }

    setNode(parent.merge({ nodes }));
  }

  // Push a new node, as current parent. We started parsing it
  function pushNode(node) {
    stack = stack.push(node);
  }

  // Pop the current parent node. Because we're done parsing it
  function popNode() {
    const node = stack.peek();
    stack = stack.pop();
    appendNode(node);
  }

  const parser = new htmlparser.Parser(
    {
      onopentag(tagName, attribs) {
        if (BLOCK_TAGS[tagName]) {
          const type = BLOCK_TAGS[tagName];
          const block = Block.create({
            data: getData(tagName, attribs),
            isVoid: isVoid(type),
            type,
          });

          pushNode(block);
        } else if (INLINE_TAGS[tagName]) {
          const type = INLINE_TAGS[tagName];
          const inline = Inline.create({
            data: getData(tagName, attribs),
            isVoid: isVoid(type),
            type,
          });

          pushNode(inline);
        } else if (MARK_TAGS[tagName]) {
          const mark = Mark.create({
            data: getData(tagName, attribs),
            type: MARK_TAGS[tagName],
          });

          marks = marks.add(mark);
        } else if (tagName == 'br') {
          const textNode = Text.create({
            text: '\n',
            marks,
          });
          appendNode(textNode);
        }
        // else ignore

        // Parse marks from the class name
        const newMarks = getMarksForClassName(attribs.class);
        marks = marks.concat(newMarks);
      },

      ontext(text) {
        const cleanText = sanitizeSpaces(text);
        const textNode = Text.create({ text: cleanText, marks });
        appendNode(textNode);
      },

      onclosetag(tagName) {
        if (BLOCK_TAGS[tagName] || INLINE_TAGS[tagName]) {
          const parent = stack.peek();

          // Special rule for code blocks that we must split in lines
          if (parent.type === BLOCKS.CODE) {
            let lines = splitLines(parent.text);
            // Remove trailing newline
            if (lines.last().trim() === '') {
              lines = lines.skipLast(1);
            }
            setNode(
              parent.merge({
                nodes: lines.map(line =>
                  Block.create({
                    type: BLOCKS.CODE_LINE,
                    nodes: [Text.create(line)],
                  })
                ),
              })
            );
          }

          popNode();
        } else if (MARK_TAGS[tagName]) {
          const type = MARK_TAGS[tagName];
          marks = marks.filter(mark => mark.type !== type);
        }
        // else ignore
      },
    },
    {
      decodeEntities: true,
    }
  );

  parser.write(str);
  parser.end();

  if (stack.size !== 1) {
    throw new Error(
      'Invalid HTML. A tag might not have been closed correctly.'
    );
  }

  return stack.peek();
}

export default parse;
